import { lazy } from 'react';
import type { RouteObject } from 'react-router-dom';
import { ProtectedRoute } from '../components/ProtectedRoute';

const Home = lazy(() => import('../pages/home/page'));
const Login = lazy(() => import('../pages/auth/login/page'));
const Signup = lazy(() => import('../pages/auth/signup/page'));
const EmployeeSignup = lazy(() => import('../pages/auth/employee-signup/page'));
const IndividualSignup = lazy(() => import('../pages/auth/individual-signup/page'));
const ResetPassword = lazy(() => import('../pages/auth/reset-password/page'));
const VerifyEmail = lazy(() => import('../pages/auth/verify-email/page'));
const EmployeeWelcome = lazy(() => import('../pages/employee/welcome/page'));
const IndividualWelcome = lazy(() => import('../pages/individual/welcome/page'));
const AdminSetup = lazy(() => import('../pages/admin/setup/page'));
const AdminWelcome = lazy(() => import('../pages/admin/welcome/page'));
const AdminDashboard = lazy(() => import('../pages/admin/dashboard/page'));
const AdminAnalytics = lazy(() => import('../pages/admin/analytics/page'));
const AdminSettings = lazy(() => import('../pages/admin/settings/page'));
const AdminSimulations = lazy(() => import('../pages/admin/simulations/page'));
const AdminReports = lazy(() => import('../pages/admin/reports/page'));
const AdminBilling = lazy(() => import('../pages/admin/billing/page'));
const AdminCampaigns = lazy(() => import('../pages/admin/campaigns/page'));
const AdminCompliance = lazy(() => import('../pages/admin/compliance/page'));
const StaffManagement = lazy(() => import('../pages/admin/staff/page'));
const EmployeeProfile = lazy(() => import('../pages/admin/staff/employee-profile/page'));
const EmployeeDashboard = lazy(() => import('../pages/employee/dashboard/page'));
const EmployeeProgress = lazy(() => import('../pages/employee/progress/page'));
const EmployeeNudges = lazy(() => import('../pages/employee/nudges/page'));
const EmployeeResources = lazy(() => import('../pages/employee/resources/page'));
const EmployeeSimulation = lazy(() => import('../pages/employee/simulation/page'));
const SimulationReflection = lazy(() => import('../pages/employee/simulation/reflection/page'));
const EmployeeThreatDemo = lazy(() => import('../pages/employee/threat-demo/page'));
const EmployeeQuiz = lazy(() => import('../pages/employee/quiz/page'));
const EmployeeSecurityTips = lazy(() => import('../pages/employee/security-tips/page'));
const EmployeeAchievements = lazy(() => import('../pages/employee/achievements/page'));
const EmployeeVideos = lazy(() => import('../pages/employee/videos/page'));
const EmployeeNews = lazy(() => import('../pages/employee/news/page'));
const EmployeeProfilePage = lazy(() => import('../pages/employee/profile/page'));
const EmployeeLeaderboard = lazy(() => import('../pages/employee/leaderboard/page'));
const EmployeeNotifications = lazy(() => import('../pages/employee/notifications/page'));
const EmployeeCalendar = lazy(() => import('../pages/employee/calendar/page'));
const IndividualDashboard = lazy(() => import('../pages/individual/dashboard/page'));
const IndividualSettings = lazy(() => import('../pages/individual/settings/page'));
const IndividualProgress = lazy(() => import('../pages/individual/progress/page'));
const IndividualResources = lazy(() => import('../pages/individual/resources/page'));
const IndividualSimulation = lazy(() => import('../pages/individual/simulation/page'));
const IndividualSimulationReflection = lazy(() => import('../pages/individual/simulation/reflection/page'));
const IndividualNews = lazy(() => import('../pages/individual/news/page'));
const IndividualVideos = lazy(() => import('../pages/individual/videos/page'));
const IndividualAchievements = lazy(() => import('../pages/individual/achievements/page'));
const IndividualThreatDemo = lazy(() => import('../pages/individual/threat-demo/page'));
const IndividualQuiz = lazy(() => import('../pages/individual/quiz/page'));
const IndividualSecurityTips = lazy(() => import('../pages/individual/security-tips/page'));
const IndividualNotifications = lazy(() => import('../pages/individual/notifications/page'));
const IndividualCalendar = lazy(() => import('../pages/individual/calendar/page'));
const IndividualNudges = lazy(() => import('../pages/individual/nudges/page'));
const IndividualProfile = lazy(() => import('../pages/individual/profile/page'));
const SharedSettings = lazy(() => import('../pages/shared/settings/page'));
const SharedHelpSupport = lazy(() => import('../pages/shared/help/page'));
const PlatformLogin = lazy(() => import('../pages/platform/login/page'));
const PlatformLayout = lazy(() => import('../layout/platform/PlatformLayout'));
const PlatformDashboard = lazy(() => import('../pages/platform/dashboard/page'));
const PlatformUsers = lazy(() => import('../pages/platform/users/page'));
const PlatformUserDetail = lazy(() => import('../pages/platform/users/detail/page'));
const PlatformOrganisations = lazy(() => import('../pages/platform/organisations/page'));
const PlatformOrgDetail = lazy(() => import('../pages/platform/organisations/detail/page'));
const PlatformSimulations = lazy(() => import('../pages/platform/simulations/page'));
const PlatformSimulationDetail = lazy(() => import('../pages/platform/simulations/detail/page'));
const PlatformSentInstances = lazy(() => import('../pages/platform/simulations/instances/page'));
const PlatformNudges = lazy(() => import('../pages/platform/nudges/page'));
const PlatformNudgeInteractions = lazy(() => import('../pages/platform/nudges/interactions/page'));
const PlatformAnalytics = lazy(() => import('../pages/platform/analytics/page'));
const PlatformSettings = lazy(() => import('../pages/platform/settings/page'));
const PlatformBilling = lazy(() => import('../pages/platform/billing/page'));
const PlatformPlans = lazy(() => import('../pages/platform/plans/page'));
const PlatformAuditLogs = lazy(() => import('../pages/platform/audit/page'));
const PlatformEmailTemplates = lazy(() => import('../pages/platform/email-templates/page'));
const PlatformContentManagement = lazy(() => import('../pages/platform/content/page'));
const PlatformSystemHealth = lazy(() => import('../pages/platform/system-health/page'));
const PlatformSupportTickets = lazy(() => import('../pages/platform/support/page'));
const PlatformAnnouncements = lazy(() => import('../pages/platform/announcements/page'));
const PlatformApiManagement = lazy(() => import('../pages/platform/api/page'));
const PlatformFeatureFlags = lazy(() => import('../pages/platform/feature-flags/page'));
const NotFound = lazy(() => import('../pages/NotFound'));

const routes: RouteObject[] = [
  {
    path: '/',
    element: <Login />,
  },
  {
    path: '/home',
    element: <Home />,
  },
  {
    path: '/login',
    element: <Login />,
  },
  {
    path: '/signup',
    element: <Signup />,
  },
  {
    path: '/register-employee',
    element: <EmployeeSignup />,
  },
  {
    path: '/register-individual',
    element: <IndividualSignup />,
  },
  {
    path: '/reset-password',
    element: <ResetPassword />,
  },
  {
    path: '/verify-email',
    element: <VerifyEmail />,
  },
  {
    path: '/employee/welcome',
    element: <EmployeeWelcome />,
  },
  {
    path: '/individual/welcome',
    element: <IndividualWelcome />,
  },
  {
    path: '/admin/setup',
    element: <ProtectedRoute allowedRoles={['org_admin']}><AdminSetup /></ProtectedRoute>,
  },
  {
    path: '/admin/welcome',
    element: <AdminWelcome />,
  },
  {
    path: '/admin/dashboard',
    element: <ProtectedRoute allowedRoles={['org_admin']}><AdminDashboard /></ProtectedRoute>,
  },
  {
    path: '/admin/analytics',
    element: <ProtectedRoute allowedRoles={['org_admin']}><AdminAnalytics /></ProtectedRoute>
  },
  {
    path: '/admin/settings',
    element: <ProtectedRoute allowedRoles={['org_admin']}><AdminSettings /></ProtectedRoute>
  },
  {
    path: '/admin/reports',
    element: <ProtectedRoute allowedRoles={['org_admin']}><AdminReports /></ProtectedRoute>
  },
  {
    path: '/admin/billing',
    element: <ProtectedRoute allowedRoles={['org_admin']}><AdminBilling /></ProtectedRoute>,
  },
  {
    path: '/admin/campaigns',
    element: <ProtectedRoute allowedRoles={['org_admin']}><AdminCampaigns /></ProtectedRoute>,
  },
  {
    path: '/admin/compliance',
    element: <ProtectedRoute allowedRoles={['org_admin']}><AdminCompliance /></ProtectedRoute>,
  },
  {
    path: '/admin/staff',
    element: <ProtectedRoute allowedRoles={['org_admin']}><StaffManagement /></ProtectedRoute>,
  },
  {
    path: '/admin/staff/:id',
    element: <ProtectedRoute allowedRoles={['org_admin']}><EmployeeProfile /></ProtectedRoute>,
  },
  {
    path: '/employee/dashboard',
    element: <ProtectedRoute allowedRoles={['employee']}><EmployeeDashboard /></ProtectedRoute>,
  },
  {
    path: '/employee/progress',
    element: <ProtectedRoute allowedRoles={['employee']}><EmployeeProgress /></ProtectedRoute>
  },
  {
    path: '/employee/resources',
    element: <ProtectedRoute allowedRoles={['employee']}><EmployeeResources /></ProtectedRoute>
  },
  {
    path: '/employee/nudges',
    element: <ProtectedRoute allowedRoles={['employee']}><EmployeeNudges /></ProtectedRoute>,
  },
  {
    path: '/employee/simulation',
    element: <ProtectedRoute allowedRoles={['employee']}><EmployeeSimulation /></ProtectedRoute>,
  },
  {
    path: '/employee/simulation/reflection',
    element: <ProtectedRoute allowedRoles={['employee']}><SimulationReflection /></ProtectedRoute>,
  },
  {
    path: '/employee/settings',
    element: <ProtectedRoute allowedRoles={['employee']}><SharedSettings userRole="employee" /></ProtectedRoute>,
  },
  {
    path: '/employee/help',
    element: <ProtectedRoute allowedRoles={['employee']}><SharedHelpSupport userRole="employee" /></ProtectedRoute>,
  },
  {
    path: '/employee/achievements',
    element: <ProtectedRoute allowedRoles={['employee']}><EmployeeAchievements /></ProtectedRoute>,
  },
  {
    path: '/employee/videos',
    element: <ProtectedRoute allowedRoles={['employee']}><EmployeeVideos /></ProtectedRoute>,
  },
  {
    path: '/employee/news',
    element: <ProtectedRoute allowedRoles={['employee']}><EmployeeNews /></ProtectedRoute>,
  },
  {
    path: '/employee/profile',
    element: <ProtectedRoute allowedRoles={['employee']}><EmployeeProfilePage /></ProtectedRoute>,
  },
  {
    path: '/employee/leaderboard',
    element: <ProtectedRoute allowedRoles={['employee']}><EmployeeLeaderboard /></ProtectedRoute>,
  },
  {
    path: '/employee/notifications',
    element: <ProtectedRoute allowedRoles={['employee']}><EmployeeNotifications /></ProtectedRoute>,
  },
  {
    path: '/employee/calendar',
    element: <ProtectedRoute allowedRoles={['employee']}><EmployeeCalendar /></ProtectedRoute>,
  },
  {
    path: '/employee/threat-demo',
    element: <ProtectedRoute allowedRoles={['employee']}><EmployeeThreatDemo /></ProtectedRoute>,
  },
  {
    path: '/employee/quiz',
    element: <ProtectedRoute allowedRoles={['employee']}><EmployeeQuiz /></ProtectedRoute>,
  },
  {
    path: '/employee/security-tips',
    element: <ProtectedRoute allowedRoles={['employee']}><EmployeeSecurityTips /></ProtectedRoute>,
  },
  {
    path: '/individual/dashboard',
    element: <ProtectedRoute allowedRoles={['individual']}><IndividualDashboard /></ProtectedRoute>,
  },
  {
    path: '/individual/settings',
    element: <ProtectedRoute allowedRoles={['individual']}><IndividualSettings /></ProtectedRoute>,
  },
  {
    path: '/individual/preferences',
    element: <ProtectedRoute allowedRoles={['individual']}><SharedSettings userRole="individual" /></ProtectedRoute>,
  },
  {
    path: '/individual/help',
    element: <ProtectedRoute allowedRoles={['individual']}><SharedHelpSupport userRole="individual" /></ProtectedRoute>,
  },
  {
    path: '/individual/progress',
    element: <ProtectedRoute allowedRoles={['individual']}><IndividualProgress /></ProtectedRoute>,
  },
  {
    path: '/individual/resources',
    element: <ProtectedRoute allowedRoles={['individual']}><IndividualResources /></ProtectedRoute>,
  },
  {
    path: '/individual/simulation',
    element: <ProtectedRoute allowedRoles={['individual']}><IndividualSimulation /></ProtectedRoute>,
  },
  {
    path: '/individual/simulation/reflection',
    element: <ProtectedRoute allowedRoles={['individual']}><IndividualSimulationReflection /></ProtectedRoute>,
  },
  {
    path: '/individual/news',
    element: <ProtectedRoute allowedRoles={['individual']}><IndividualNews /></ProtectedRoute>,
  },
  {
    path: '/individual/videos',
    element: <ProtectedRoute allowedRoles={['individual']}><IndividualVideos /></ProtectedRoute>,
  },
  {
    path: '/individual/achievements',
    element: <ProtectedRoute allowedRoles={['individual']}><IndividualAchievements /></ProtectedRoute>,
  },
  {
    path: '/individual/notifications',
    element: <ProtectedRoute allowedRoles={['individual']}><IndividualNotifications /></ProtectedRoute>,
  },
  {
    path: '/individual/calendar',
    element: <ProtectedRoute allowedRoles={['individual']}><IndividualCalendar /></ProtectedRoute>,
  },
  {
    path: '/individual/nudges',
    element: <ProtectedRoute allowedRoles={['individual']}><IndividualNudges /></ProtectedRoute>,
  },
  {
    path: '/individual/profile',
    element: <ProtectedRoute allowedRoles={['individual']}><IndividualProfile /></ProtectedRoute>,
  },
  {
    path: '/individual/threat-demo',
    element: <ProtectedRoute allowedRoles={['individual']}><IndividualThreatDemo /></ProtectedRoute>,
  },
  {
    path: '/individual/quiz',
    element: <ProtectedRoute allowedRoles={['individual']}><IndividualQuiz /></ProtectedRoute>,
  },
  {
    path: '/individual/security-tips',
    element: <ProtectedRoute allowedRoles={['individual']}><IndividualSecurityTips /></ProtectedRoute>,
  },
  // Alias routes for backward compatibility and nicer URLs
  {
    path: '/individual/training',
    element: <ProtectedRoute allowedRoles={['individual']}><IndividualSimulation /></ProtectedRoute>,
  },
  {
    path: '/platform/login',
    element: <PlatformLogin />,
  },
  {
    element: <ProtectedRoute allowedRoles={['platform_admin']}><PlatformLayout /></ProtectedRoute>,
    children: [
      {
        path: '/platform/dashboard',
        element: <PlatformDashboard />,
      },
      {
        path: '/platform/users',
        element: <PlatformUsers />,
      },
      {
        path: '/platform/users/:userType/:userId',
        element: <PlatformUserDetail />,
      },
      {
        path: '/platform/organisations',
        element: <PlatformOrganisations />,
      },
      {
        path: '/platform/organisations/:orgId',
        element: <PlatformOrgDetail />,
      },
      {
        path: '/platform/simulations',
        element: <PlatformSimulations />,
      },
      {
        path: '/platform/simulations/instances',
        element: <PlatformSentInstances />,
      },
      {
        path: '/platform/simulations/:templateId',
        element: <PlatformSimulationDetail />,
      },
      {
        path: '/platform/nudges',
        element: <PlatformNudges />,
      },
      {
        path: '/platform/nudges/interactions',
        element: <PlatformNudgeInteractions />,
      },
      {
        path: '/platform/analytics',
        element: <PlatformAnalytics />,
      },
      {
        path: '/platform/billing',
        element: <PlatformBilling />,
      },
      {
        path: '/platform/plans',
        element: <PlatformPlans />,
      },
      {
        path: '/platform/audit',
        element: <PlatformAuditLogs />,
      },
      {
        path: '/platform/email-templates',
        element: <PlatformEmailTemplates />,
      },
      {
        path: '/platform/content',
        element: <PlatformContentManagement />,
      },
      {
        path: '/platform/system-health',
        element: <PlatformSystemHealth />,
      },
      {
        path: '/platform/support',
        element: <PlatformSupportTickets />,
      },
      {
        path: '/platform/announcements',
        element: <PlatformAnnouncements />,
      },
      {
        path: '/platform/api',
        element: <PlatformApiManagement />,
      },
      {
        path: '/platform/feature-flags',
        element: <PlatformFeatureFlags />,
      },
      {
        path: '/platform/settings',
        element: <PlatformSettings />,
      },
    ],
  },
  {
    path: '*',
    element: <NotFound />,
  },
];

export default routes;
